import * as React from "react";
import DOMPurify, { Config } from "dompurify";

/**
 * Comprehensive Input Sanitization Library
 *
 * This library provides security-focused input sanitization utilities
 * for the mangomap-ui component library. It prevents XSS attacks,
 * validates data types, and ensures safe handling of user inputs.
 */

// ==============================================
// TYPE DEFINITIONS
// ==============================================

export interface SanitizeTextOptions {
	maxLength?: number;
	allowNewlines?: boolean;
	trimWhitespace?: boolean;
	removeHtmlTags?: boolean;
}

export interface SanitizeNumberOptions {
	min?: number;
	max?: number;
	allowFloat?: boolean;
	defaultValue?: number | null;
}

export interface SanitizeUrlOptions {
	allowedProtocols?: string[];
	maxLength?: number;
	requireProtocol?: boolean;
}

export interface SanitizeEmailOptions {
	maxLength?: number;
	allowInternational?: boolean;
}

export interface SanitizeSearchOptions {
	maxLength?: number;
	allowSpecialChars?: boolean;
	trimWhitespace?: boolean;
}

export type SanitizationSchema = Record<
	string,
	{
		type:
			| "text"
			| "html"
			| "number"
			| "price"
			| "percentage"
			| "url"
			| "email"
			| "search";
		options?: any;
	}
>;

// ==============================================
// HTML & TEXT SANITIZATION
// ==============================================

/**
 * Sanitizes HTML content to prevent XSS attacks
 */
export function sanitizeHtml(html: string, options: Config = {}): string {
	if (typeof html !== "string") {
		return "";
	}

	const defaultOptions: Config = {
		ALLOWED_TAGS: [
			"p",
			"br",
			"strong",
			"em",
			"u",
			"h1",
			"h2",
			"h3",
			"h4",
			"h5",
			"h6",
			"ul",
			"ol",
			"li",
			"a",
			"blockquote",
			"code",
			"pre",
		],
		ALLOWED_ATTR: ["href", "target", "rel"],
		ALLOW_DATA_ATTR: false,
		FORBID_TAGS: ["script", "style", "iframe", "embed", "object"],
		FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "style"],
	};

	return DOMPurify.sanitize(html, { ...defaultOptions, ...options });
}

/**
 * Sanitizes plain text input by removing potentially dangerous characters
 */
export function sanitizeText(
	text: string,
	options: SanitizeTextOptions = {},
): string {
	if (typeof text !== "string") {
		return "";
	}

	const {
		maxLength = 1000,
		allowNewlines = true,
		trimWhitespace = true,
		removeHtmlTags = true,
	} = options;

	let sanitized = text;

	// Remove HTML tags if not allowed
	if (removeHtmlTags) {
		sanitized = sanitized.replaceAll(/<[^>]*>/g, "");
	}

	// Remove potentially dangerous characters
	sanitized = sanitized.replaceAll(/[<>'"]/g, "");

	// Handle newlines
	if (!allowNewlines) {
		sanitized = sanitized.replaceAll(/[\r\n]/g, " ");
	}

	// Trim whitespace
	if (trimWhitespace) {
		sanitized = sanitized.trim();
	}

	// Enforce maximum length
	if (sanitized.length > maxLength) {
		sanitized = sanitized.slice(0, Math.max(0, maxLength));
	}

	return sanitized;
}

// ==============================================
// NUMERIC SANITIZATION
// ==============================================

/**
 * Sanitizes and validates numeric input
 */
export function sanitizeNumber(
	value: any,
	options: SanitizeNumberOptions = {},
): number | null {
	const {
		min = Number.NEGATIVE_INFINITY,
		max = Number.POSITIVE_INFINITY,
		allowFloat = true,
		defaultValue = null,
	} = options;

	// Handle different input types
	let numValue: number;
	if (typeof value === "string") {
		// Remove non-numeric characters except decimal point and minus sign
		const cleaned = value.replaceAll(/[^0-9.-]/g, "");
		numValue = allowFloat
			? Number.parseFloat(cleaned)
			: Number.parseInt(cleaned, 10);
	} else if (typeof value === "number") {
		numValue = value;
	} else {
		return defaultValue;
	}

	// Check for NaN
	if (Number.isNaN(numValue)) {
		return defaultValue;
	}

	// Check for Infinity
	if (!Number.isFinite(numValue)) {
		return defaultValue;
	}

	// Enforce min/max bounds
	if (numValue < min) {
		numValue = min;
	}
	if (numValue > max) {
		numValue = max;
	}

	return numValue;
}

/**
 * Sanitizes currency/price input
 */
export function sanitizePrice(
	value: any,
	options: Partial<SanitizeNumberOptions> = {},
): number | null {
	const { maxValue = 999_999.99, minValue = 0 } = options as {
		maxValue?: number;
		minValue?: number;
	};

	const sanitized = sanitizeNumber(value, {
		min: minValue,
		max: maxValue,
		allowFloat: true,
		defaultValue: null,
	});

	// Round to 2 decimal places for currency
	return sanitized === null ? null : Math.round(sanitized * 100) / 100;
}

/**
 * Sanitizes percentage input
 */
export function sanitizePercentage(
	value: any,
	options: Partial<SanitizeNumberOptions> = {},
): number | null {
	const { maxValue = 100, minValue = 0 } = options as {
		maxValue?: number;
		minValue?: number;
	};

	return sanitizeNumber(value, {
		min: minValue,
		max: maxValue,
		allowFloat: true,
		defaultValue: null,
	});
}

// ==============================================
// URL SANITIZATION
// ==============================================

/**
 * Sanitizes URL input to prevent malicious links
 */
export function sanitizeUrl(
	url: string,
	options: SanitizeUrlOptions = {},
): string | null {
	if (typeof url !== "string") {
		return null;
	}

	const {
		allowedProtocols = ["http:", "https:", "mailto:"],
		maxLength = 2048,
		requireProtocol = false,
	} = options;

	let sanitized = url.trim();

	// Enforce maximum length
	if (sanitized.length > maxLength) {
		return null;
	}

	// Remove dangerous characters
	sanitized = sanitized.replaceAll(/[<>"']/g, "");

	// Check for protocol
	try {
		// If no protocol, assume https for validation
		const urlToCheck = sanitized.includes("://")
			? sanitized
			: `https://${sanitized}`;
		const urlObj = new URL(urlToCheck);

		// Check if protocol is allowed
		if (!allowedProtocols.includes(urlObj.protocol)) {
			return null;
		}

		// Return original if it had protocol, or add https if not required
		if (sanitized.includes("://")) {
			return sanitized;
		} else if (requireProtocol) {
			return `https://${sanitized}`;
		} else {
			return sanitized; // Return without protocol if not required
		}
	} catch {
		return null;
	}
}

// ==============================================
// EMAIL SANITIZATION
// ==============================================

/**
 * Sanitizes and validates email input
 */
export function sanitizeEmail(
	email: string,
	options: SanitizeEmailOptions = {},
): string | null {
	if (typeof email !== "string") {
		return null;
	}

	const { maxLength = 254, allowInternational = true } = options;

	let sanitized = email.trim().toLowerCase();

	// Enforce maximum length (RFC 5321 limit)
	if (sanitized.length > maxLength) {
		return null;
	}

	// Basic email pattern validation
	const emailPattern = allowInternational
		? /^[^\s@]+@[^\s@]+\.[^\s@]+$/
		: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

	if (!emailPattern.test(sanitized)) {
		return null;
	}

	// Remove potentially dangerous characters
	sanitized = sanitized.replaceAll(/[<>"']/g, "");

	return sanitized;
}

// ==============================================
// SEARCH QUERY SANITIZATION
// ==============================================

/**
 * Sanitizes search query input
 */
export function sanitizeSearchQuery(
	query: string,
	options: SanitizeSearchOptions = {},
): string {
	if (typeof query !== "string") {
		return "";
	}

	const {
		maxLength = 200,
		allowSpecialChars = false,
		trimWhitespace = true,
	} = options;

	let sanitized = query;

	// Remove HTML tags
	sanitized = sanitized.replaceAll(/<[^>]*>/g, "");

	// Remove potentially dangerous characters
	if (!allowSpecialChars) {
		sanitized = sanitized.replaceAll(/[<>'"]/g, "");
	}

	// Trim whitespace
	if (trimWhitespace) {
		sanitized = sanitized.trim();
	}

	// Enforce maximum length
	if (sanitized.length > maxLength) {
		sanitized = sanitized.slice(0, Math.max(0, maxLength));
	}

	return sanitized;
}

// ==============================================
// FORM DATA SANITIZATION
// ==============================================

/**
 * Sanitizes an entire form data object
 */
export function sanitizeFormData(
	formData: Record<string, any>,
	schema: SanitizationSchema,
): Record<string, any> {
	if (!formData || typeof formData !== "object") {
		return {};
	}

	const sanitized: Record<string, any> = {};

	for (const [key, value] of Object.entries(formData)) {
		const fieldSchema = schema[key];

		if (!fieldSchema) {
			// If no schema defined, apply basic text sanitization
			sanitized[key] = sanitizeText(value);
			continue;
		}

		switch (fieldSchema.type) {
			case "text": {
				sanitized[key] = sanitizeText(value, fieldSchema.options);
				break;
			}
			case "html": {
				sanitized[key] = sanitizeHtml(value, fieldSchema.options);
				break;
			}
			case "number": {
				sanitized[key] = sanitizeNumber(value, fieldSchema.options);
				break;
			}
			case "price": {
				sanitized[key] = sanitizePrice(value, fieldSchema.options);
				break;
			}
			case "percentage": {
				sanitized[key] = sanitizePercentage(value, fieldSchema.options);
				break;
			}
			case "url": {
				sanitized[key] = sanitizeUrl(value, fieldSchema.options);
				break;
			}
			case "email": {
				sanitized[key] = sanitizeEmail(value, fieldSchema.options);
				break;
			}
			case "search": {
				sanitized[key] = sanitizeSearchQuery(value, fieldSchema.options);
				break;
			}
			default: {
				sanitized[key] = sanitizeText(value);
			}
		}
	}

	return sanitized;
}

// ==============================================
// VALIDATION HELPERS
// ==============================================

/**
 * Validates if a value is safe (not containing potential XSS)
 */
export function isSafeValue(value: string): boolean {
	if (typeof value !== "string") {
		return false;
	}

	// Check for potential XSS patterns
	const dangerousPatterns = [
		/<script/i,
		/javascript:/i,
		/on\w+\s*=/i,
		/<iframe/i,
		/<embed/i,
		/<object/i,
		/data:\s*text\/html/i,
	];

	return !dangerousPatterns.some((pattern) => pattern.test(value));
}

/**
 * Creates a sanitization middleware for React components
 */
export function createSanitizationMiddleware<T>(
	sanitizer: (value: T, options?: any) => T,
	options: any = {},
): (value: T, onChange?: (value: T) => void) => T {
	return (value: T, onChange?: (value: T) => void): T => {
		const sanitized = sanitizer(value, options);
		onChange?.(sanitized);
		return sanitized;
	};
}

// ==============================================
// PREDEFINED SANITIZERS
// ==============================================

export const SANITIZERS = {
	// Common form fields
	name: (value: any) =>
		sanitizeText(value, { maxLength: 100, allowNewlines: false }),
	description: (value: any) =>
		sanitizeText(value, { maxLength: 1000, allowNewlines: true }),
	search: (value: any) => sanitizeSearchQuery(value),

	// Numeric fields
	price: (value: any) => sanitizePrice(value),
	quantity: (value: any) =>
		sanitizeNumber(value, { min: 0, allowFloat: false }),
	percentage: (value: any) => sanitizePercentage(value),

	// Web fields
	email: (value: any) => sanitizeEmail(value),
	url: (value: any) => sanitizeUrl(value),

	// Content fields
	richText: (value: any) => sanitizeHtml(value),
	plainText: (value: any) => sanitizeText(value, { removeHtmlTags: true }),
} as const;

// ==============================================
// REACT HOOKS
// ==============================================

/**
 * React hook for sanitized input handling
 */
export function useSanitizedInput<T>(
	initialValue: T,
	sanitizer: (value: T, options?: any) => T = sanitizeText as any,
	options: any = {},
): [T, (newValue: T) => void, T] {
	const [value, setValue] = React.useState<T>(initialValue);
	const [sanitizedValue, setSanitizedValue] = React.useState<T>(() =>
		sanitizer(initialValue, options),
	);

	const handleChange = React.useCallback(
		(newValue: T) => {
			setValue(newValue);
			const sanitized = sanitizer(newValue, options);
			setSanitizedValue(sanitized);
		},
		[sanitizer, options],
	);

	return [value, handleChange, sanitizedValue];
}

export default {
	sanitizeHtml,
	sanitizeText,
	sanitizeNumber,
	sanitizePrice,
	sanitizePercentage,
	sanitizeUrl,
	sanitizeEmail,
	sanitizeSearchQuery,
	sanitizeFormData,
	isSafeValue,
	createSanitizationMiddleware,
	SANITIZERS,
	useSanitizedInput,
};
